import React, { ComponentProps, ComponentType, ElementType, type JSX } from 'react'
import ReactMarkdown, { uriTransformer } from 'react-markdown'
import { Text, OrderedList, Heading, ListProps, UnorderedList, LinkProps } from '~/design_system'

type ReactMarkdownComponents = ComponentProps<typeof ReactMarkdown>['components']

type MarkdownProps = {
  children: string
  allowedElements?: string[]
  components?: Record<string, ComponentType>
}

/**
 * wrapper for DS support of ReactMarkdown
 *
 * syntax notes: https://commonmark.org/help/
 */
export function MarkdownDisplay(props: MarkdownProps) {
  const mergedComponents = {
    ul: InsideAlignUnorderedList,
    ol: InsideAlignOrderedList,
    h1: header('h1'),
    h2: header('h2'),
    h3: header('h3'),
    h4: header('h4'),
    h5: header('h5'),
    a: CommentLink,
    ...props.components
  } as Record<string, ComponentType<React.PropsWithChildren>>

  const mergedAllowedElements = [
    'p', // can't really leave this one out
    'strong',
    'em',
    // Currently not supporting headers by default, but if you pass them into "allowedElements" you can turn them on
    // 'h1', 'h2', 'h3', 'h4', 'h5',
    'a',
    'ul',
    'li',
    'ol',
    ...(props.allowedElements || [])
  ]

  return (
    <ReactMarkdown
      components={mergedComponents as ReactMarkdownComponents}
      allowedElements={mergedAllowedElements}
      unwrapDisallowed
      transformLinkUri={urlTransform}>
      {props.children}
    </ReactMarkdown>
  )
}

/** BEGIN CUSTOM RENDER COMPONENTS * */

function urlTransform(value: string) {
  if (value.match(/^(tag|mention):/)) {
    return value
  }
  // TODO (vic) - add recognition of tag URL here, something like `userid:` or `grouptag:`
  return uriTransformer(value)
}

function CommentLink(props: LinkProps) {
  // href here is output from transformLinkUri function
  // TODO (vic) - recognize "tag url" in props.href and render tag component instead
  // We are not supporting real hyperlinking for the time being, it is arguably unsafe
  // Maybe ignore children and repeat the url directly if we really want that
  return <Text as="span">{props.children}</Text>
}

/**
 * Because the absurd "outside" default is crap and we all know it's crap
 */
function InsideAlignUnorderedList(props: ListProps) {
  return (
    <UnorderedList stylePosition="inside" {...props}>
      {props.children}
    </UnorderedList>
  )
}

function InsideAlignOrderedList(props: ListProps) {
  return (
    <OrderedList stylePosition="inside" {...props}>
      {props.children}
    </OrderedList>
  )
}

function header(tag: ElementType) {
  const Header = ({ children }: { children: JSX.Element }) => <Heading as={tag}>{children}</Heading>
  Header.displayName = `MarkdownHeader(${String(tag)})`
  return Header
}
